<template>
  <Tippy class="UIDropdown" v-bind="props" :theme="dropdownTheme" :tag="'div'">
    <template #default="{ state }">
      <slot name="toggle" :state="state" :isVisible="state.isVisible">
        <UIDropdownToggle :isOpen="state.isVisible">
          <slot />
        </UIDropdownToggle>
      </slot>
    </template>

    <template #content="{ hide }">
      <slot v-if="!isDisabled" name="content" :hide="hide">
        <div :class="['UIDropdown__menu', contentClass]" :style="styles">
          <slot name="before-content" :hide="hide" />
          <ul class="UIDropdown__menu-list">
            <slot
              v-for="(option, index) in items"
              :key="index"
              name="option-container"
              :option="option"
              :hide="hide"
            >
              <UIDropdownListItem
                @click.prevent="() => handleClickOption(option, hide)"
              >
                <slot name="option" :option="option" :hide="hide">
                  <button>
                    {{ option.label }}
                  </button>
                </slot>
              </UIDropdownListItem>
            </slot>
          </ul>
          <slot name="after-content" :hide="hide" />
        </div>
      </slot>
    </template>
  </Tippy>
</template>

<script lang="ts" setup generic="T">
import { computed } from 'vue'

import { useRouter } from 'vue-router'
import { Tippy } from 'vue-tippy'

import UIDropdownListItem from './UIDropdownListItem.vue'
import UIDropdownToggle from './UIDropdownToggle.vue'

import type { IIcons } from '../icons/types'
import type { Options as PopperOptions, Placement } from '@popperjs/core'
import type { RouteLocationRaw } from 'vue-router'

export type IUIDropdownOption = {
  label: string
  value: unknown
  to?: RouteLocationRaw
  href?: string
  icon?: IIcons
  isHidden?: boolean

  onClick?: (item?: unknown) => void
}

type ITippyProps = InstanceType<typeof Tippy>['$props']
interface Props extends /* @vue-ignore */ ITippyProps {
  label?: string
  interactive?: boolean

  appendTo?: Element | 'parent' | ((ref: Element) => Element)
  offset?: [number, number]
  items: T[]
  istooltip?: boolean
  trigger?: string
  placement?: Placement
  isDisabled?: boolean
  duration?: [number, number]
  theme?: string
  arrow?: boolean
  fontSize?: string
  contentClass?: string
  contentStyle?: object
  width?: string
  dropdownTheme?: 'gradient-picker' | 'dropdown-evergpt'
  popperOptions?: Partial<PopperOptions>
}

const emit = defineEmits<{
  (eventName: 'onSelect', value: T): void
}>()
const props = withDefaults(defineProps<Props>(), {
  appendTo: () => () => document.body,
  interactive: true,
  offset: () => [0, 1],
  trigger: 'click',
  placement: 'bottom-start',
  duration: () => [0, 0],
  theme: 'dropdown-evergpt',
  arrow: false,
  fontSize: '16px',
  contentClass: '',
  contentStyle: () => ({}),
  dropdownTheme: 'dropdown-evergpt',
})

const router = useRouter()
const handleClickOption = (option: T, hide: () => void) => {
  if (option.onClick) {
    option.onClick()
  }
  if (option.to) {
    router.push(option.to)
  }
  emit('onSelect', option)
  hide()
}

const styles = computed(() => ({
  '--dropdown-width': props.width,
  'font-size': props.fontSize,
  ...props.contentStyle,
}))
</script>

<style scoped lang="scss">
.UIDropdown {
  display: inline-block;
  --toggle-size: 1rem;
}

.UIDropdown__menu {
  width: var(--dropdown-width);
  border-radius: 4px;
  padding: 0.5rem;
  overflow: hidden;
  box-shadow: 0 0 10px -2px rgba(9, 9, 45, 0.15);
  background: $color-white;

  .UIDropdown__menu-list {
    @include customScrollbar;
    display: flex;
    flex-direction: column;
    font-size: inherit;
    max-height: 25rem;
    gap: var(---fds-global-space-xxs);
    li {
      list-style: none;
    }
  }
}
</style>

<!-- Override tippy dropdown theme -->
<style lang="scss">
.tippy-box[data-theme~='dropdown-evergpt'] {
  box-shadow: 0px 4px 8px -4px #0000001a;
  box-shadow: 0px 8px 16px -3px #0000001a;
  font-size: 0.875rem;
  line-height: 1.65rem;
  border-radius: 12px;
  overflow: hidden;
  background: transparent;
  padding: 0;
  color: $color-neutral-170;
  width: 100%;
  max-width: unset !important;
  border: 1px solid $color-neutral-10;

  .tippy-content {
    @include font-size-12px;
    padding: 0;
    max-width: unset !important;
    a {
      color: $color-neutral-170;
      text-decoration: none;
    }
  }
}
.tippy-box[data-theme~='gradient-picker'] {
  box-shadow: 0px 5px 10px -2px #09092d26;
  border-radius: 4px;
  overflow: hidden;
  background: $color-white;
  color: $color-neutral-170;

  .tippy-content {
    @include font-size-14px;
    padding: 0.75rem;
  }
}
</style>
